import { useEffect, type ReactNode } from 'react'
import { BASE } from '../api'
import type { Provider } from '../types'

const PROVIDER_NAME: Record<Provider, string> = {
  aws: 'AWS', azure: 'AZURE', oci: 'OCI', core42: 'CORE42', alibaba: 'ALIBABA', gcp: 'GOOGLE', nebius: 'NEBIUS', mistral: 'MISTRAL', telnyx: 'TELNYX', snowflake: 'SNOWFLAKE', cloudflare: 'CLOUDFLARE', databricks: 'DATABRICKS', gemini: 'GEMINI API', hfendpoints: 'HF ENDPOINTS', watsonx: 'WATSONX', xai: 'XAI', together: 'TOGETHER', crusoe: 'CRUSOE', huawei: 'HUAWEI', groq: 'GROQ', fireworks: 'FIREWORKS', deepinfra: 'DEEPINFRA',
  openai: 'OPENAI', anthropic: 'ANTHROPIC',
}

// the vendors' logos are the vendor pages' own files (web/public/vendors): these are SVG, the rest PNG
const SVG_LOGO = new Set(['azure', 'groq', 'fireworks', 'hfendpoints', 'nebius', 'openai', 'snowflake'])
/** The address of a vendor's logo, served from this site (no request to another origin). */
export const vendorLogo = (provider: string) => `${BASE}vendors/${provider}.${SVG_LOGO.has(provider) ? 'svg' : 'png'}`

/** The vendor's logo and name in a pill, the same purple chip for every vendor (the logo added 2026-10-17, so the
 *  Calculator's side-by-side columns and Compare's cards show it). */
export function ProviderTag({ provider }: { provider: Provider }) {
  return <span className={`tag vendor ${provider}`}><img src={vendorLogo(provider)} alt="" width={14} height={14} />{PROVIDER_NAME[provider]}</span>
}

/** "N regions" with the list on hover or keyboard focus. A count with no way
 *  to see which regions would just be a number to argue about. */
export function RegionCount({ regions, source, what }: {
  regions: string[]
  source?: 'price-list' | 'oracle-docs'
  what?: string
}) {
  const n = regions.length
  if (n === 0) return <span className="tag plain">no regions</span>
  const isGlobal = n === 1 && regions[0] === 'global'
  const label = isGlobal ? 'global price' : `${n} region${n === 1 ? '' : 's'}`
  return (
    <span className="regions" tabIndex={0}>
      <span className="tag plain">{label}</span>
      <span className="pop" role="tooltip">
        <b>{what ?? 'Available in'}</b>
        {isGlobal ? (
          <span>One list price in every commercial region.</span>
        ) : (
          <ul>{regions.map((r) => <li key={r}>{r}</li>)}</ul>
        )}
        {source === 'oracle-docs' ? <i>per Oracle’s model-by-region page</i> : null}
      </span>
    </span>
  )
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag plain">{children}</span>
}

export function Stat({
  label, value, foot, win,
}: { label: string; value: ReactNode; foot?: ReactNode; win?: boolean }) {
  return (
    <div className={win ? 'stat win' : 'stat'}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {foot ? <div className="foot">{foot}</div> : null}
    </div>
  )
}

export function Field({
  label, children, hint,
}: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="field">
      <label>{label}{hint ? <span className="muted"> · {hint}</span> : null}</label>
      {children}
    </div>
  )
}

export const usd = (n: number, digits = 2) =>
  n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })

/** Prices span six orders of magnitude, so pick a sensible precision. */
export const usdAuto = (n: number) =>
  n >= 100 ? usd(n, 0) : n >= 1 ? usd(n, 2) : usd(n, n >= 0.01 ? 3 : 4)

export const compact = (n: number) =>
  n.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 })

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : String(error)
  return <p className="err small">{message}</p>
}


/** Model picker grouped by family (Meta, Qwen, OpenAI ...) with the models
 *  underneath. Families are alphabetical; inside a family, models are in the
 *  order the API gave them (most-sold first, then by downloads). */
export function ModelSelect<T extends { canonical: string; family: string }>({
  models, value, onChange, render, minWidth = 300,
}: {
  models: T[]
  value: string
  onChange: (canonical: string) => void
  render: (m: T) => string
  minWidth?: number
}) {
  const groups = new Map<string, T[]>()
  for (const m of models) {
    const list = groups.get(m.family) ?? []
    list.push(m)
    groups.set(m.family, list)
  }
  // Known families alphabetically; the long tail last, under "Other".
  const families = [...groups.keys()].sort((a, b) =>
    a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b))
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ minWidth }}>
      {families.map((f) => (
        <optgroup key={f} label={`${f} (${groups.get(f)!.length})`}>
          {groups.get(f)!.map((m) => (
            <option key={m.canonical} value={m.canonical}>{render(m)}</option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}

/** How a model gets onto an OCI dedicated AI cluster: Oracle's own build, or
 *  weights you import. Shown wherever a cluster is priced, because the two are
 *  billed on different meters and only one carries a minimum commitment. */
export function DacRoute({ via }: { via: 'pretrained' | 'import' }) {
  return via === 'import'
    ? <span className="tag plain" title="You import the weights from Hugging Face; Oracle hosts them. Billed on the Model Import meter, no 744-hour minimum.">import model</span>
    : <span className="tag plain" title="Oracle's own build of the model, from the pretrained catalogue. Billed on the vendor's dedicated meter; 744 unit-hour minimum (a month's commitment) applies.">foundation model</span>
}


/** Family filter for the model pickers: "All families" or one organisation. */
export function FamilyFilter({ families, value, onChange }: {
  families: { family: string; count: number }[]
  value: string
  onChange: (family: string) => void
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ minWidth: 150 }}>
      <option value="">All families</option>
      {families.map((f) => (
        <option key={f.family} value={f.family}>{f.family} ({f.count})</option>
      ))}
    </select>
  )
}


/** A small "?" that explains the page or panel it sits next to, on hover or
 *  keyboard focus. Text only; anything longer belongs in the docs. */
/** The "?" explainers are gone: the Demo button on each page covers what the
 *  help text said. The component stays so call sites need no change; the text
 *  is kept for screen readers only. */
export function Help({ text, label = 'What this does' }: { text: string; label?: string }) {
  return <span className="sr-only" role="note" aria-label={label}>{text}</span>
}

/** Bytes per weight, as the server's VRAM arithmetic uses them (tools.BYTES_PER_PARAM). */
const PRECISION_BYTES: Record<string, number> = { fp32: 4, fp16: 2, bf16: 2, fp8: 1, int8: 1, int4: 0.5 }
const PRECISION_ORDER = ['fp32', 'fp16', 'bf16', 'fp8', 'int8', 'int4']

/** The precisions a model can be served at besides its native one: only
 *  quantizations below it. Upcasting (fp32 for a bf16 model, bf16 for gpt-oss's
 *  4-bit weights) costs memory and adds no accuracy, so it is not offered.
 *  With no known native precision every choice stays open. */
export function precisionChoices(native: string | null | undefined): string[] {
  if (!native || !(native in PRECISION_BYTES)) return PRECISION_ORDER
  return PRECISION_ORDER.filter((p) => PRECISION_BYTES[p] < PRECISION_BYTES[native])
}

/** The precision picker every page uses, built from the chosen model: "native"
 *  first (what the weights ship in, as Hugging Face reports it), then the
 *  quantizations below it. A choice the new model cannot take -- switching
 *  from a bf16 model at fp8 to one that ships in fp8 -- falls back to native. */
export function PrecisionSelect({ native, raw, publishedGb, value, onChange }: {
  native: string | null | undefined
  raw?: string | null
  /** GB of weight files the repo publishes, where its precision is not known: the model is sized from them */
  publishedGb?: number | null
  value: string
  onChange: (precision: string) => void
}) {
  const choices = precisionChoices(native)
  useEffect(() => {
    if (native !== undefined && value && !choices.includes(value)) onChange('')
  }, [native]) // eslint-disable-line react-hooks/exhaustive-deps
  const shipped = native === undefined ? '…'
    : native ? `${native}${raw && raw.toLowerCase() !== native ? ` · ${raw}` : ''}`
      : publishedGb ? `as published · ${publishedGb.toLocaleString()} GB` : 'unknown, fp16 assumed'
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      title="Native is the precision the weights are published in; below it, quantized versions. Upcasting is not offered. A model whose format the repository does not name is sized from the weight files it publishes.">
      <option value="">native ({shipped})</option>
      {choices.map((p) => <option key={p} value={p}>{p}{native ? ' · quantized' : ''}</option>)}
    </select>
  )
}

/** How an instance's GPUs are joined. Shown only where it matters: a box with
 *  more than one GPU. NVLink/NVSwitch or Infinity Fabric can carry a model
 *  split across the GPUs; PCIe cannot, so such a box only serves models that
 *  fit in one of its GPUs. */
export function Interconnect({ value, count }: { value: 'nvlink' | 'infinity-fabric' | 'pcie'; count: number }) {
  if (count <= 1) return null
  const label = value === 'nvlink' ? 'NVLink' : value === 'infinity-fabric' ? 'Infinity Fabric' : 'PCIe'
  const title = value === 'pcie'
    ? 'GPUs joined over PCIe only: a model must fit in one GPU; it cannot be split across them'
    : `GPUs joined by ${label}: a model can be split across them`
  return <span className={`tag plain ic ${value}`} title={title}>{label}</span>
}

export type Light = 'live' | 'wait' | 'stale' | 'off' | 'none'
/** The site's live pill, as a button: lit green while what it stands for answers or is fresh, amber when it is
 *  behind, red when it does not answer; pressing it checks again. `none`: switched off, a quiet label. */
export function LiveLight({ light, word, busy, onCheck, title = 'Check again now' }: {
  light: Light; word: string; busy: boolean; onCheck: () => void; title?: string
}) {
  if (light === 'none') return <span className="live connlight quiet"><span className="led wait" aria-hidden="true" />{word}</span>
  return (
    <button type="button" className={`live connlight${light === 'stale' ? ' stale' : light === 'off' ? ' off' : ''}`} disabled={busy}
      title={title} onClick={onCheck}>
      <span className={light === 'wait' ? 'led wait' : 'led'} aria-hidden="true" />{light === 'live' ? <b>{word}</b> : word}
    </button>
  )
}
